import { OnboardingStatusResponse } from "@/services/brand.service";

export const ONBOARDING_SKIP_KEY = "brand_onboarding_skip_to_dashboard";

/**
 * Onboarding walkthrough clips live in their own public Supabase bucket
 * (`onboarding-videos`), separate from the `reveshare-assets` bucket that
 * holds user uploads. File names are keyed by step id below.
 */
export const ONBOARDING_VIDEO_BUCKET = "onboarding-videos";

export function onboardingVideoUrl(fileName: string): string {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(
    /\/+$/,
    ""
  );
  if (!base) return "";
  return `${base}/storage/v1/object/public/${ONBOARDING_VIDEO_BUCKET}/${fileName}`;
}

/** Video file per step id (see BRAND_ONBOARDING_STEP_DEFS for the titles). */
export const ONBOARDING_STEP_VIDEO_FILES: Record<number, string> = {
  1: "01_complete_your_company_profile.mp4",
  2: "02_manage_your_affiliate_programs.mp4",
  3: "03_setup_your_post_checkout_conversions.mp4",
  4: "04_manage_your_product_campaigns.mp4",
  5: "05_manage_your_affiliates.mp4",
};

export const ONBOARDING_STEP_VIDEOS: Record<number, string> = Object.fromEntries(
  Object.entries(ONBOARDING_STEP_VIDEO_FILES).map(([id, file]) => [
    Number(id),
    onboardingVideoUrl(file),
  ])
);

export interface BrandOnboardingStepDef {
  id: number;
  title: string;
  description: string;
  path: string;
  requiresPostCheckoutFeature?: boolean;
}

export interface BrandOnboardingStep extends BrandOnboardingStepDef {
  completed: boolean;
  videoUrl: string;
}

/** Ordered to match the walkthrough clips. */
export const BRAND_ONBOARDING_STEP_DEFS: BrandOnboardingStepDef[] = [
  {
    id: 1,
    title: "Complete your company profile",
    description:
      "Add your company details, timezone and social links so affiliates know who they are working with.",
    path: "/brand/settings?tab=company-details",
  },
  {
    id: 2,
    title: "Manage your affiliate programs",
    description:
      "Set commission rates and choose which programs are open to everyone and which are VIP only.",
    path: "/brand/programs",
  },
  {
    id: 3,
    title: "Set up post-checkout conversions",
    description:
      "Invite customers to become affiliates right after they buy from your store.",
    path: "/brand/post-checkout",
    requiresPostCheckoutFeature: true,
  },
  {
    id: 4,
    title: "Manage your product campaigns",
    description:
      "Launch campaigns with discount codes and rewards that affiliates can promote.",
    path: "/brand/campaigns",
  },
  {
    id: 5,
    title: "Manage your affiliates",
    description:
      "Invite your first affiliate and keep track of everyone promoting your brand.",
    path: "/brand/affiliates/invite",
  },
];

export function hasSkippedOnboardingToDashboard(): boolean {
  try {
    return sessionStorage.getItem(ONBOARDING_SKIP_KEY) === "1";
  } catch {
    return false;
  }
}

export function skipOnboardingToDashboard(): void {
  try {
    sessionStorage.setItem(ONBOARDING_SKIP_KEY, "1");
  } catch {
    // ignore
  }
}

export function clearOnboardingSkip(): void {
  try {
    sessionStorage.removeItem(ONBOARDING_SKIP_KEY);
  } catch {
    // ignore
  }
}

export function isStepCompleted(
  stepId: number,
  data: OnboardingStatusResponse
): boolean {
  switch (stepId) {
    case 1:
      return !!data.companyProfileStat?.hasCompanyProfile;
    case 2:
      return !!data.programsStat?.hasPrograms;
    case 3:
      return !!data.postCheckoutStat?.hasPostCheckoutConfig;
    case 4:
      return (data.campaignStats?.totalCampaigns || 0) > 0;
    case 5:
      return !!data.affiliateStat?.hasAffiliates;
    default:
      return false;
  }
}

export function getVisibleStepDefs(
  hasPostCheckoutFeature: boolean
): BrandOnboardingStepDef[] {
  return BRAND_ONBOARDING_STEP_DEFS.filter(
    (step) =>
      !step.requiresPostCheckoutFeature || hasPostCheckoutFeature
  );
}

export function buildOnboardingSteps(
  data: OnboardingStatusResponse | null,
  hasPostCheckoutFeature: boolean
): BrandOnboardingStep[] {
  return getVisibleStepDefs(hasPostCheckoutFeature).map((def) => ({
    ...def,
    completed: data ? isStepCompleted(def.id, data) : false,
    videoUrl: ONBOARDING_STEP_VIDEOS[def.id] ?? "",
  }));
}

export function isOnboardingComplete(steps: BrandOnboardingStep[]): boolean {
  return steps.length > 0 && steps.every((s) => s.completed);
}
